import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Space,
  Tag,
  Alert,
  Button,
  message,
  Input,
  Table,
  Modal,
  Form,
} from "antd";
import { KeyOutlined, PlusOutlined, CopyOutlined } from "@ant-design/icons";
import { supabase } from "../../../lib/supabase";

const { Paragraph, Text } = Typography;

type PartnerKeyRow = {
  id: string;
  label: string | null;
  secret_hint: string;
  created_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
};

/** Admin UI to generate / revoke partner API keys (effp_…). */
export const PartnerApiKeysPanel: React.FC = () => {
  const [keys, setKeys] = useState<PartnerKeyRow[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [form] = Form.useForm<{ label?: string }>();

  const copyText = async (text: string, okMsg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(okMsg);
    } catch {
      message.error("Could not copy");
    }
  };

  const loadKeys = async () => {
    setKeysLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "partner-api-keys",
        { body: { action: "list" } },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setKeys((data?.keys ?? []) as PartnerKeyRow[]);
    } catch (err) {
      console.error(err);
      message.error(
        err instanceof Error ? err.message : "Failed to load API keys",
      );
    } finally {
      setKeysLoading(false);
    }
  };

  useEffect(() => {
    void loadKeys();
  }, []);

  const handleCreate = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "partner-api-keys",
        {
          body: {
            action: "create",
            label: values.label?.trim() || null,
          },
        },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setNewToken(data?.key?.token ?? null);
      setCreateOpen(false);
      form.resetFields();
      await loadKeys();
    } catch (err) {
      message.error(
        err instanceof Error ? err.message : "Failed to create key",
      );
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = (keyId: string) => {
    Modal.confirm({
      title: "Revoke this API key?",
      content: "Integrations using this key will stop working immediately.",
      okText: "Revoke",
      okButtonProps: { danger: true },
      onOk: async () => {
        const { data, error } = await supabase.functions.invoke(
          "partner-api-keys",
          { body: { action: "revoke", key_id: keyId } },
        );
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        message.success("Key revoked");
        await loadKeys();
      },
    });
  };

  return (
    <>
      <Card
        title={
          <Space>
            <KeyOutlined />
            <span>Partner API keys</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            Generate key
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          These keys can resolve any sender and read any company&apos;s
          bookings. Treat them like production secrets. Create here → copy once
          → give to the integration builder.
        </Paragraph>
        <Table
          size="small"
          rowKey="id"
          loading={keysLoading}
          dataSource={keys}
          pagination={false}
          columns={[
            {
              title: "Label",
              dataIndex: "label",
              render: (v: string | null) => v || "—",
            },
            {
              title: "Hint",
              dataIndex: "secret_hint",
              render: (v: string) => <Text code>…{v}</Text>,
            },
            {
              title: "Created",
              dataIndex: "created_at",
              render: (v: string) => v?.slice(0, 19).replace("T", " ") || "—",
            },
            {
              title: "Last used",
              dataIndex: "last_used_at",
              render: (v: string | null) =>
                v ? v.slice(0, 19).replace("T", " ") : "—",
            },
            {
              title: "Status",
              key: "status",
              render: (_: unknown, row: PartnerKeyRow) =>
                row.revoked_at ? (
                  <Tag>revoked</Tag>
                ) : (
                  <Tag color="green">active</Tag>
                ),
            },
            {
              title: "",
              key: "actions",
              render: (_: unknown, row: PartnerKeyRow) =>
                row.revoked_at ? null : (
                  <Button
                    danger
                    type="link"
                    size="small"
                    onClick={() => handleRevoke(row.id)}
                  >
                    Revoke
                  </Button>
                ),
            },
          ]}
        />
      </Card>

      <Modal
        title="Generate partner API key"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={creating}
        okText="Generate"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="label"
            label="Label"
            extra="e.g. WhatsApp builder — production"
          >
            <Input placeholder="Optional label" maxLength={120} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Copy your API key"
        open={!!newToken}
        onCancel={() => setNewToken(null)}
        footer={[
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => newToken && copyText(newToken, "API key copied")}
          >
            Copy key
          </Button>,
          <Button key="done" onClick={() => setNewToken(null)}>
            Done
          </Button>,
        ]}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="This token is shown once. Store it in a password manager."
        />
        <Input.TextArea
          value={newToken ?? ""}
          readOnly
          autoSize={{ minRows: 3, maxRows: 6 }}
          style={{ fontFamily: "monospace", fontSize: 12 }}
        />
      </Modal>
    </>
  );
};
